import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import BottomDrawer from '../Drawer/Drawer';
import { pxToRem } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import SectionLoader from '../Loader/SectionLoader';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';

const TierTable = styled.div`
  width: 100%;
  background: ${themeColors.colorWhite};
  overflow: hidden;
`;

const TierRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: ${pxToRem(12)} 0;
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};
  font-size: 14px;

  &:last-child {
    border-bottom: none;
  }

  ${({ header }) =>
    header &&
    `
      font-weight: 500;
      font-size: 12px;
      color: ${themeColors.colorTextSecondary};
      border-bottom: none;
      padding: ${pxToRem(8)} 0;

      > div:last-child {
      text-align: right;
      }
  `}
`;

const CurrentTier = styled.span`
  background-color: ${themeColors.colorTextSecondary};
  color: ${themeColors.colorWhite};
  font-size: 12px;
  font-weight: 400;
  border-radius: ${variables.borderRadius.borderRadiusSm};
  padding: ${pxToRem(2)} ${pxToRem(4)};
  margin-left: ${pxToRem(8)};
`;

const DollarAmountValue = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${themeColors.colorTextSecondary};
`;

const PricePerUnitValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${themeColors.colorTextPrimary};
  text-align: right;
`;

const RemainingToNextTierInfo = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${themeColors.colorTextPrimary};
  margin-top: 8px;
`;

const PriceproductTiers = ({
  open,
  setOpen,
  // isDeliveryAddressSelected,
  storageProduct,
  productTiers,
  currentSpend,
  isLoading,
}) => {
  const { t } = useTranslation();

  const handleClose = () => {
    // if (tempSelectedAddress?.AddressUid) {
    //   setSelectedAddress(tempSelectedAddress); // commit selection to parent
    // }
    setOpen(false);
  };

  const formatValue = (input) => {
    const value = Number(input);
    if (value >= 1_000_000) {
      // If it's an exact million, show 1M, otherwise show rounded millions
      return `$${
        (value / 1_000_000) % 1 === 0
          ? value / 1_000_000
          : Math.round(value / 1_000_000)
      }M`;
    }
    if (value >= 1_000) {
      // If it's 1000k, show 1M instead
      if (Math.round(value / 1_000) === 1000) return '$1M';
      return `$${Math.round(value / 1_000)}k`;
    }
    return `$${value}`;
  };

  const formatRange = (low, high) => {
    if (high === null) return `Over ${formatValue(low)}`;
    if (low === 0) return `Up to ${formatValue(high)}`;
    return `${formatValue(low)} to ${formatValue(high)}`;
  };

  const getCurrentTierIndex = () =>
    productTiers?.Tiers.findIndex(
      (t) =>
        currentSpend >= t.LowBound &&
        (t.HighBound === null || currentSpend < t.HighBound),
    );

  const currentTierIndex = getCurrentTierIndex();

  return (
    <BottomDrawer
      isOpen={open}
      onClose={handleClose}
      title={t('buyWizard.volumeDiscountPriceTiers')}
      subtitle={storageProduct?.ShortCaption}
    >
      <SectionLoader isLoading={isLoading}>
        <TierTable>
          <TierRow header>
            <div>{t('buyWizard.tiers')}</div>
            <div>{t('buyWizard.dollarAmount')}</div>
            <div>{t('buyWizard.pricePerUnit')}</div>
          </TierRow>

          {productTiers?.Tiers.map((tier, index) => {
            const isCurrent = index === currentTierIndex;
            const nextTier = productTiers?.Tiers[index + 1];
            const nextLow = nextTier?.LowBound ?? null;

            // Calculate amount to reach next tier
            const remaining = nextLow ? nextLow - currentSpend : null;

            return (
              // eslint-disable-next-line react/no-array-index-key
              <TierRow key={index}>
                <div>
                  {index + 1}
                  {isCurrent && <CurrentTier>Current Tier</CurrentTier>}
                  {isCurrent && remaining > 0 && (
                    <RemainingToNextTierInfo>
                      {t('buyWizard.addAmountToNextTier', {
                        amount: formatMoneyNumeral(remaining),
                      })}
                    </RemainingToNextTierInfo>
                  )}
                </div>
                <DollarAmountValue>
                  {formatRange(tier.LowBound, tier.HighBound)}
                </DollarAmountValue>
                <PricePerUnitValue>
                  ${tier.PricePerUnit.toFixed(2)}
                </PricePerUnitValue>
              </TierRow>
            );
          })}
        </TierTable>
      </SectionLoader>
    </BottomDrawer>
  );
};

PriceproductTiers.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  wizardData: PropTypes.shape({
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
    }),
  }),
  selectedAddress: PropTypes.shape({}),
  storageProduct: PropTypes.shape({
    ShortCaption: PropTypes.string,
  }),
  productTiers: PropTypes.shape({
    Tiers: PropTypes.arrayOf({}),
  }),
  isLoading: PropTypes.bool,
  currentSpend: PropTypes.number,
};

export default PriceproductTiers;
